import React, { useContext } from 'react';
import { AppContext } from '../../App';
import Badge from 'react-bootstrap/Badge';
import Spinner from 'react-bootstrap/Spinner';
import GlassWorkspaceShell from '../../components/Glass/GlassWorkspaceShell';
import LabNameForm from '../../components/Lab/LabNameForm';
import LabLogoForm from '../../components/Lab/LabLogoForm';
import StockOpenDigestRecipientForm from '../../components/Lab/StockOpenDigestRecipientForm';
import {
  getAccountRoleLabel,
  isLabAdmin,
  isLabViewer,
} from '../../utils/userAccountRole';
import './UserPageStyle.css';
import '../../components/Lab/LabLogoBadge.css';

/**
 * Detail row for lab settings (matches Account page layout).
 */
const LabDetailRow = ({ label, value, emphasis = false }) => (
  <div
    className={`account-page__row${emphasis ? ' account-page__row--emphasis' : ''}`}
  >
    <dt className="account-page__label">{label}</dt>
    <dd className="account-page__value">{value}</dd>
  </div>
);

/**
 * Lab tenant settings — logo, display name, digest recipient (Admin editable), and read-only slug.
 */
const LabPage = () => {
  const { userDetails, isSupplier, currentLab } = useContext(AppContext);
  const labAdmin = isLabAdmin(userDetails, isSupplier);
  const labViewer = isLabViewer(userDetails, isSupplier);
  const roleKind = labViewer ? 'viewer' : labAdmin ? 'admin' : 'staff';
  const roleLabel = getAccountRoleLabel(userDetails, isSupplier);

  return (
    <GlassWorkspaceShell theme="yellow">
      <div className="account-page">
        <div className="glass-panel account-page__profile">
          <div className="lab-page__logo-header">
            <LabLogoForm lab={currentLab} />
            <div className="account-page__header mb-0 flex-grow-1">
              <h1 className="glass-page-heading mb-0">Lab</h1>
              <Badge
                className={`account-page__role-badge account-page__role-badge--${roleKind}`}
              >
                {roleLabel}
              </Badge>
            </div>
          </div>

          {!currentLab ? (
            <div className="account-page__loading py-3">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading lab…
            </div>
          ) : (
            <>
              <p className="glass-page-sub mb-3">
                Your organization in Materiah. Inventory, orders, and projects
                are scoped to this lab automatically.
              </p>

              <dl className="account-page__details">
                {labAdmin ? (
                  <div className="account-page__row account-page__row--emphasis">
                    <LabNameForm initialName={currentLab.name} />
                  </div>
                ) : (
                  <LabDetailRow
                    label="Lab name"
                    value={currentLab.name}
                    emphasis
                  />
                )}
                <LabDetailRow
                  label="Slug"
                  value={
                    <span title="Stable identifier for support.">
                      {currentLab.slug}
                    </span>
                  }
                />
                <StockOpenDigestRecipientForm />
              </dl>

              {!labAdmin ? (
                <p className="glass-page-sub mb-0 mt-2">
                  Only lab Admins can rename the lab, change the logo, or set the
                  daily stock-open digest recipient.
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </GlassWorkspaceShell>
  );
};

export default LabPage;
